import maplibregl, { Map as MapLibreMap, Popup } from "maplibre-gl";
import qs from "qs";
import React, { useEffect, useRef, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import api from "../api";
import { useFilters } from "./FilterContext";

interface StateEvents {
  state: string;
  eventCount: number;
}

const ByState: React.FC = () => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);

  const [stateEvents, setStateEvents] = useState<StateEvents[]>([]);
  const [usStatesGeoJson, setUsStatesGeoJson] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { filters } = useFilters();

  // ---- 1. Fetch the data from /dashboard/by-state ----
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const { data: response } = await api.get("/dashboard/by-state", {
          params: {
            routeIds: filters.routeIds, eventTypes: filters.eventTypes, ...(filters.startDate && { startDate: filters.startDate }),
            ...(filters.endDate && { endDate: filters.endDate })
          },
          paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "comma" }),
        });

        setStateEvents(
          response.map((item: any) => ({
            state: item.state,
            eventCount: Number(item.eventCount),
          }))
        );
      } catch (err) {
        console.error("Error fetching by-state data:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [filters]);

  // ---- 2. Load GeoJSON once, uppercase the 'name' property ----
  useEffect(() => {
    fetch("/us-states.geojson")
      .then((res) => res.json())
      .then((geoJson) => {
        geoJson.features.forEach((feat: any) => {
          if (feat.properties?.name) {
            feat.properties.name = feat.properties.name.toUpperCase();
          }
        });
        setUsStatesGeoJson(geoJson);
      })
      .catch((err) => console.error("Error loading us-states.geojson:", err));
  }, []);

  // ---- 3. Initialize the map once ----
  useEffect(() => {
    if (!mapContainerRef.current) return;

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: {
        version: 8,
        sources: {},
        layers: [
          {
            id: "background",
            type: "background",
            paint: { "background-color": "#FFFFFF" },
          },
        ],
      },
      center: [-95.7129, 37.0902],
      zoom: 3.5,
    });

    mapRef.current = map;

    // Cleanup on unmount
    return () => {
      map.remove();
    };
  }, []);

  // ---- 4. Whenever GeoJSON or stateEvents changes, draw/refresh layers ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !usStatesGeoJson || stateEvents.length === 0) return;

    // If a layer/source already exists, remove it
    if (map.getSource("states-src")) {
      if (map.getLayer("states-layer")) {
        map.removeLayer("states-layer");
      }
      map.removeSource("states-src");
    }

    // Add new GeoJSON source
    map.addSource("states-src", {
      type: "geojson",
      data: usStatesGeoJson,
    });

    // Calculate coloring logic
    const maxCount = Math.max(...stateEvents.map((s) => s.eventCount), 0);
    const colorMap: { [key: string]: string } = {};
    stateEvents.forEach(({ state, eventCount }) => {
      const ratio = maxCount === 0 ? 0 : eventCount / maxCount;
      colorMap[state] = getThreeColorScale(ratio); // define your color function
    });

    // Build a paint expression
    const paintExpression: any = ["match", ["get", "name"]];
    Object.entries(colorMap).forEach(([stateName, color]) => {
      paintExpression.push(stateName, color);
    });
    paintExpression.push("rgb(200, 200, 200)"); // default color

    // Add a fill layer
    map.addLayer({
      id: "states-layer",
      type: "fill",
      source: "states-src",
      paint: {
        "fill-color": paintExpression,
        "fill-opacity": 1.0,
      },
    });

    // 5. Add a popup on hover
    const popup = new Popup({
      closeButton: false,
      closeOnClick: false,
    });

    function onMouseMove(e: maplibregl.MapMouseEvent) {
      const features = map!.queryRenderedFeatures(e.point, {
        layers: ["states-layer"],
      });
      if (!features.length) {
        popup.remove();
        return;
      }

      const feature = features[0];
      const stateName = feature.properties?.name;
      const match = stateEvents.find((s) => s.state === stateName);
      const eventCount = match ? match.eventCount : 0;

      popup
        .setLngLat(e.lngLat)
        .setHTML(`<strong>${stateName}</strong><br/>Events: ${eventCount}`)
        .addTo(map!);
    }

    function onMouseLeave() {
      popup.remove();
    }

    map.on("mousemove", "states-layer", onMouseMove);
    map.on("mouseleave", "states-layer", onMouseLeave);

    // Cleanup
    return () => {
      map.off("mousemove", "states-layer", onMouseMove);
      map.off("mouseleave", "states-layer", onMouseLeave);
    };
  }, [usStatesGeoJson, stateEvents]);

  return (
    <div className="bg-white rounded-lg shadow-md w-full h-[680px] overflow-hidden p-6">
      <h3 className="text-left text-2xl font-bold text-gray-800 mb-2">
        Events by State
      </h3>

      {/* 
         1) The map container is always here 
         2) If `isLoading` is true, we show a spinner *on top* 
      */}
      <div className="relative w-full h-[600px] rounded-md">
        {/* Map container for MapLibre */}
        <div ref={mapContainerRef} className="w-full h-full" />

        {/* Spinner overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10">
            <TailSpin
              height={80}
              width={80}
              color="#2563eb"
              strokeWidth={2}
              ariaLabel="loading-spinner"
              visible={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Example color interpolator
function getThreeColorScale(ratio: number) {
  // #FFFFCC → #FEB24C → #F03B20
  if (ratio <= 0.5) return interpolateColor([255, 255, 204], [254, 178, 76], ratio / 0.5);
  return interpolateColor([254, 178, 76], [240, 59, 32], (ratio - 0.5) / 0.5);
}

function interpolateColor(
  color1: [number, number, number],
  color2: [number, number, number],
  t: number
) {
  const r = Math.round(color1[0] + (color2[0] - color1[0]) * t);
  const g = Math.round(color1[1] + (color2[1] - color1[1]) * t);
  const b = Math.round(color1[2] + (color2[2] - color1[2]) * t);
  return `rgb(${r}, ${g}, ${b})`;
}

export default ByState;
